import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Crown, Loader2, Search, Timer } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage, throwIfResNotOk } from "@/lib/queryClient";
import { OfferWindowsCard } from "./OfferWindowsCard";
import { BuyBoxAutoAdminCard } from "./BuyBoxAutoAdminCard";
import { LifetimeFulfillmentCard } from "./LifetimeFulfillmentCard";

// Total allowance after the grant (stacked SYRE units of 5).
const LIFETIME_ALLOWANCES = ["5", "10", "15", "20"] as const;

interface ResearchTotals {
  month: string;
  byType: Array<{ type: string; provider: string; searches: number; estimatedCostCents: number }>;
  heavyAccounts: Array<{ accountId: number; searches: number }>;
}

function currentMonthUtc(): string {
  return new Date().toISOString().slice(0, 7);
}

function centsToUsd(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function parseUserId(raw: string): number | null {
  const n = Number(raw.trim());
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Admin → Billing v3: SYRE offer windows, grant lifetime, shorten a test trial, research totals, daily buy box agents. */
export function BillingV3Panel() {
  return (
    <div className="space-y-6" data-testid="panel-admin-billing-v3">
      <div>
        <h3 className="text-lg font-semibold">Billing v3</h3>
        <p className="text-sm text-muted-foreground">
          Per-property billing tools. Every change here is written to the admin audit log.
        </p>
      </div>
      <OfferWindowsCard />
      <LifetimeFulfillmentCard />
      <GrantLifetimeCard />
      <ShortenTrialCard />
      <ResearchTotalsCard />
      <BuyBoxAutoAdminCard />
    </div>
  );
}

function GrantLifetimeCard() {
  const { toast } = useToast();
  const [userId, setUserId] = useState("");
  const [allowance, setAllowance] = useState<(typeof LIFETIME_ALLOWANCES)[number]>("5");
  const [source, setSource] = useState("syre-2026");
  const [paymentReference, setPaymentReference] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const parsedUserId = parseUserId(userId);
  const canSubmit = parsedUserId !== null && source.trim().length >= 2;

  const grantMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/billing/grant-lifetime", {
        userId: parsedUserId,
        allowance: Number(allowance),
        source: source.trim(),
        paymentReference: paymentReference.trim() || null,
      });
      return (await res.json()) as { ok: boolean; userId: number; allowance: number; source: string };
    },
    onSuccess: (data) => {
      setConfirmOpen(false);
      toast({ title: "Lifetime granted", description: `User ${data.userId} now has a ${data.allowance}-property lifetime plan.` });
      setUserId("");
      setPaymentReference("");
    },
    onError: (err) => {
      setConfirmOpen(false);
      toast({ title: "Couldn't grant lifetime", description: parseErrorMessage(err, "Please check the user ID and try again."), variant: "destructive" });
    },
  });

  return (
    <Card className="p-4 space-y-4" data-testid="card-admin-grant-lifetime">
      <div className="flex items-center gap-2">
        <Crown className="h-4 w-4 text-brand" />
        <h4 className="font-semibold">Grant lifetime</h4>
      </div>
      <p className="text-sm text-muted-foreground">
        For in-person SYRE sales and June founders. Protected accounts are refused by the server.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="grant-lifetime-user">User ID</Label>
          <Input id="grant-lifetime-user" inputMode="numeric" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="input-grant-lifetime-user" />
        </div>
        <div className="space-y-1.5">
          <Label>Property allowance</Label>
          <Select value={allowance} onValueChange={(v) => setAllowance(v as (typeof LIFETIME_ALLOWANCES)[number])}>
            <SelectTrigger data-testid="select-grant-lifetime-allowance">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIFETIME_ALLOWANCES.map((a) => (
                <SelectItem key={a} value={a}>{a} properties</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="grant-lifetime-source">Source</Label>
          <Input id="grant-lifetime-source" value={source} onChange={(e) => setSource(e.target.value)} data-testid="input-grant-lifetime-source" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="grant-lifetime-ref">Payment reference (optional)</Label>
          <Input id="grant-lifetime-ref" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} data-testid="input-grant-lifetime-ref" />
        </div>
      </div>
      <Button onClick={() => setConfirmOpen(true)} disabled={!canSubmit || grantMutation.isPending} data-testid="button-grant-lifetime">
        Grant lifetime…
      </Button>

      <Dialog
        open={confirmOpen}
        onOpenChange={(next) => {
          if (grantMutation.isPending) return;
          setConfirmOpen(next);
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="dialog-confirm-grant-lifetime"
          onInteractOutside={(e) => {
            if (grantMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (grantMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (grantMutation.isPending) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Grant a lifetime plan?</DialogTitle>
            <DialogDescription>
              User {parsedUserId ?? "?"} gets a {allowance}-property lifetime plan (source “{source.trim()}”). Any live
              trial subscription is canceled.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={grantMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={() => grantMutation.mutate()} disabled={grantMutation.isPending} data-testid="button-confirm-grant-lifetime">
              {grantMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Grant lifetime
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ShortenTrialCard() {
  const { toast } = useToast();
  const [userId, setUserId] = useState("");
  const [minutes, setMinutes] = useState("10");
  const parsedUserId = parseUserId(userId);
  const parsedMinutes = Number(minutes);
  const canSubmit = parsedUserId !== null && Number.isInteger(parsedMinutes) && parsedMinutes >= 2;

  const shortenMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/billing/shorten-trial", { userId: parsedUserId, minutes: parsedMinutes });
      return (await res.json()) as { ok: boolean; trialEndsAt: string };
    },
    onSuccess: (data) => {
      toast({ title: "Trial shortened", description: `Trial now ends ${new Date(data.trialEndsAt).toLocaleString()}.` });
    },
    onError: (err) => {
      toast({ title: "Couldn't shorten the trial", description: parseErrorMessage(err, "Please check the user ID and try again."), variant: "destructive" });
    },
  });

  return (
    <Card className="p-4 space-y-4" data-testid="card-admin-shorten-trial">
      <div className="flex items-center gap-2">
        <Timer className="h-4 w-4 text-brand" />
        <h4 className="font-semibold">Shorten trial</h4>
      </div>
      <p className="text-sm text-muted-foreground" data-testid="text-shorten-trial-note">
        Internal-domain accounts only. Moves a trialing v3 subscription's end to N minutes from now, for smoke tests.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="shorten-trial-user">User ID</Label>
          <Input id="shorten-trial-user" inputMode="numeric" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="input-shorten-trial-user" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="shorten-trial-minutes">Minutes from now</Label>
          <Input id="shorten-trial-minutes" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="input-shorten-trial-minutes" />
        </div>
      </div>
      <Button onClick={() => shortenMutation.mutate()} disabled={!canSubmit || shortenMutation.isPending} data-testid="button-shorten-trial">
        {shortenMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
        Shorten trial
      </Button>
    </Card>
  );
}

function ResearchTotalsCard() {
  const [month, setMonth] = useState(currentMonthUtc());
  const validMonth = /^\d{4}-\d{2}$/.test(month) ? month : currentMonthUtc();
  const totalsQuery = useQuery<ResearchTotals>({
    queryKey: ["/api/admin/billing/research-totals", validMonth],
    queryFn: async () => {
      const res = await fetch(`/api/admin/billing/research-totals?month=${encodeURIComponent(validMonth)}`, { credentials: "include" });
      await throwIfResNotOk(res);
      return (await res.json()) as ResearchTotals;
    },
  });
  const data = totalsQuery.data;
  const totalSearches = data?.byType.reduce((sum, r) => sum + r.searches, 0) ?? 0;
  const totalCost = data?.byType.reduce((sum, r) => sum + r.estimatedCostCents, 0) ?? 0;

  return (
    <Card className="p-4 space-y-4" data-testid="card-admin-research-totals">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-brand" />
          <h4 className="font-semibold">Research searches</h4>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="research-totals-month" className="text-sm">Month</Label>
          <Input
            id="research-totals-month"
            type="month"
            className="w-40"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="input-research-totals-month"
          />
        </div>
      </div>

      {totalsQuery.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : totalsQuery.isError ? (
        <p className="text-sm text-red-600" data-testid="text-research-totals-error">
          {parseErrorMessage(totalsQuery.error, "Couldn't load research totals.")}
        </p>
      ) : (
        <>
          <Table data-testid="table-research-totals">
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead className="text-right">Searches</TableHead>
                <TableHead className="text-right">Est. cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.byType ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                    No searches logged for {data?.month ?? validMonth}.
                  </TableCell>
                </TableRow>
              ) : (
                data!.byType.map((r) => (
                  <TableRow key={`${r.type}:${r.provider}`} data-testid={`row-research-total-${r.type}-${r.provider}`}>
                    <TableCell>{r.type.replace(/_/g, " ")}</TableCell>
                    <TableCell>{r.provider}</TableCell>
                    <TableCell className="text-right">{r.searches.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{centsToUsd(r.estimatedCostCents)}</TableCell>
                  </TableRow>
                ))
              )}
              {(data?.byType ?? []).length > 0 && (
                <TableRow className="font-semibold">
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell className="text-right" data-testid="text-research-total-searches">{totalSearches.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{centsToUsd(totalCost)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="space-y-2">
            <h5 className="text-sm font-semibold">Heavy accounts (over 200 searches this month)</h5>
            {(data?.heavyAccounts ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-research-heavy-empty">None.</p>
            ) : (
              <ul className="text-sm space-y-1" data-testid="list-research-heavy-accounts">
                {data!.heavyAccounts.map((a) => (
                  <li key={a.accountId} className="flex justify-between rounded border px-3 py-1.5">
                    <a href={`/admin?tab=app-users&focusUser=${a.accountId}`} className="underline">Account {a.accountId}</a>
                    <span>{a.searches.toLocaleString()} searches</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
